import { useMemo } from 'react'
import { Chart } from 'react-chartjs-2'
import type { ChartData, ChartOptions } from 'chart.js'
import { ensureChartsRegistered } from './register'
import { CHART_COLORS, useDarkMode } from './palette'

ensureChartsRegistered()

export interface PolarAreaChartProps {
  labels: string[]
  values: number[]
  colors?: readonly string[]
  height?: number | string
  className?: string
  ariaLabel?: string
}

const PALETTE = [
  '#2563eb', '#16a34a', '#dc2626', '#ca8a04', '#9333ea',
  '#0891b2', '#ea580c', '#4f46e5', '#db2777', '#0d9488',
]

export function PolarAreaChart({
  labels,
  values,
  colors,
  height = 320,
  className,
  ariaLabel,
}: PolarAreaChartProps) {
  const dark = useDarkMode()

  const data = useMemo<ChartData<'polarArea'>>(
    () => ({
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: labels.map(
            (_, i) => ((colors ?? PALETTE)[i % (colors ?? PALETTE).length] as string) + 'CC',
          ),
          borderColor: labels.map(
            (_, i) => (colors ?? PALETTE)[i % (colors ?? PALETTE).length] as string,
          ),
          borderWidth: 1,
        },
      ],
    }),
    [labels, values, colors],
  )

  const options = useMemo<ChartOptions<'polarArea'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          grid: { color: dark ? CHART_COLORS.gridDark : CHART_COLORS.gridLight },
          ticks: { color: dark ? CHART_COLORS.tickDark : CHART_COLORS.tickLight, backdropColor: 'transparent' },
        },
      },
      plugins: {
        legend: { display: true, position: 'right' },
        datalabels: { display: false },
      },
    }),
    [dark],
  )

  return (
    <div
      className={className}
      style={{ height, width: '100%' }}
      role="img"
      aria-label={ariaLabel}
    >
      <Chart type="polarArea" data={data} options={options} />
    </div>
  )
}
